// Libs
import classNames from 'classnames/bind';
import {
  Button,
  Flex,
  Heading,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Spinner,
  Table,
  Text,
  TextInput,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { pdf } from '@react-pdf/renderer';
// Components, Layouts, Pages
import { DocumentCourseReportPDF } from '~/pages/student/educationalProgram/documentPDF/DocumentCourseReportPdf';
import { BasePagination, BaseSelect } from '~/components/common';
import BaseDynamicSelect, {
  IBaseSelectPayload,
  IBaseSelectResult,
} from '~/components/common/specific/baseDynamicSelect/BaseDynamicSelect';
import ModalDeleteExam from './component/ModalDeleteExam';
import QuizAnswersExportStatus from './component/QuizAnswersExportStatus';
import { QUIZ_ANSWERS_EXPORT_NAMESPACE, useQuizAnswersExport } from './component/useQuizAnswersExport';
import { DocumentCourseActivityReportPDF } from '~/pages/student/educationalProgram/documentPDF/DocumentCourseActivityReportPdf';
// Others
import { IExamForm } from '~/utils/mockData/common';
import { ExamStatusEnum, ModuleTypeEnum } from '~/utils/enum/base';
import { defaultValuesForm, schema } from './helper';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getStudents } from '~/thunks/user/userThunk';
import { IBodyCreateExam, IBodyUpdateExam, IQueryGetExams } from '~/utils/interface/exam';
import { createExam, getListExams, updateExam, getExamById } from '~/thunks/exam/examThunk';
import { DEFAULT_LIMIT_PAGE, DEFAULT_PAGE, TIMEOUT_ALERT_MILLISECOND } from '~/utils/constants/common';
import { getCoursesDetailOfStudent, getCoursesOfStudent, getModulesOfCourse } from '~/thunks/course/courseThunk';
import { IBaseSelectOption } from '~/utils/interface/common';
import { adminRoute } from '~/utils/routes/admin';
import i18n from '~/thirdParties/i18n';
import { IReportTypeEnum } from '~/utils/enum/common';
import { generateGuid } from '~/utils/helper/common';
import { IToast } from '~/utils/interface/toast';
import { toastActions } from '~/thunks/toast/toastSlice';
import { useDebounce } from '~/utils/hooks/base';
// Styles, images, icons
import styles from './AdministrativeArea.module.scss';
import { icons } from '~/assets';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const AdministrativeArea = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  // admin/quizAnswersExport is loaded with the page, so the quiz answers button does not suspend it later.
  const { t } = useTranslation([
    'common/home',
    'common/base',
    'admin/administrativeArea',
    QUIZ_ANSWERS_EXPORT_NAMESPACE,
  ]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<IExamForm>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const { exams, isLoadingCreateExam, isRefreshListExam, hasMore } = useAppSelector((state) => state.examState);
  //#endregion Selector

  //#region Declare State
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [isExportingReport, setIsExportingReport] = useState(false);
  const [editingExamId, setEditingExamId] = useState<string | null>(null);
  const [isLoadingUpdateExam, setIsLoadingUpdateExam] = useState(false);
  const [isLoadingEditExam, setIsLoadingEditExam] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [examIdToDelete, setExamIdToDelete] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText);

  const statusOptions = [
    { value: ExamStatusEnum.PASSED, label: t('administrative_area_pass_status', { ns: 'admin/administrativeArea' }) },
    { value: ExamStatusEnum.FAILED, label: t('administrative_area_fail_status', { ns: 'admin/administrativeArea' }) },
  ];
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListExam]);

  useEffect(() => {
    setPage(DEFAULT_PAGE);
    handleGetExams({ limit: DEFAULT_LIMIT_PAGE, page: DEFAULT_PAGE, textSearch: debouncedSearch.trim() || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  //#endregion Implement Hook

  //#region Handle Function
  const handleEditExam = async (examId: string) => {
    setEditingExamId(examId);
    setIsLoadingEditExam(true);

    try {
      const res = await dispatch(getExamById(examId)).unwrap();
      const exam = res.data;

      if (!exam) {
        setIsLoadingEditExam(false);
        return;
      }

      setValue('studentId', String(exam.studentId));
      setValue('studentName', exam.studentName);
      setValue('courseId', String(exam.courseId));
      setValue('courseName', exam.courseName);
      setValue('moduleId', String(exam.moduleId));
      setValue('moduleName', exam.moduleName);
      setValue('note', exam.note ?? '');
      setValue('status', exam.status);
      setValue('pointValue', exam.pointValue.toString());
      setValue('value', exam.value);
      setValue('credits', exam.credits.toString());
    } catch (error) {
      setEditingExamId(null);
    } finally {
      setIsLoadingEditExam(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingExamId(null);
    setIsLoadingEditExam(false);
    reset();
  };

  const handleUpdateExam = async (examId: string, payload: IBodyUpdateExam) => {
    setIsLoadingUpdateExam(true);
    await dispatch(updateExam({ examId, body: payload }))
      .unwrap()
      .then((res) => {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_update_success_message', { ns: 'common/base' }),
          type: 'success',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);

        setEditingExamId(null);
        reset();
        handleGetExams();
      })
      .catch((error) => {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_update_error_message', { ns: 'common/base' }),
          type: 'error',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);
      })
      .finally(() => {
        setIsLoadingUpdateExam(false);
      });
  };

  const handleDeleteExam = (examId: string) => {
    setExamIdToDelete(examId);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setExamIdToDelete(null);
  };

  const handleDeleteSuccess = () => {
    handleGetExams();
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    handleGetExams({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
      textSearch: searchText.trim() || undefined,
    });
  };

  const handleGetExams = async (query?: IQueryGetExams) => {
    await dispatch(
      getListExams(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
          textSearch: searchText.trim() || undefined,
        }
      )
    ).unwrap();
  };

  const watchStudentId = watch('studentId');
  const watchCourseId = watch('courseId');
  // "Risposte Quiz PDF" uses the student and course chosen in the form.
  const quizAnswersExport = useQuizAnswersExport({ studentId: watchStudentId, courseId: watchCourseId });

  const fetchStudentOptions = useCallback(
    async (payload: IBaseSelectPayload): Promise<IBaseSelectResult> => {
      try {
        const resp = await dispatch(getStudents({ page: payload.page, limit: DEFAULT_LIMIT_PAGE })).unwrap();
        const optionsResp: IBaseSelectOption[] = (resp.data?.responses || []).map((student) => ({
          value: String(student.id),
          label: student.fullName,
        }));
        return { optionsResp, hasMoreResp: resp.data?.hasMore ?? false };
      } catch {
        return { optionsResp: [], hasMoreResp: false };
      }
    },
    [dispatch]
  );

  const fetchCourseOptions = useCallback(
    async (payload: IBaseSelectPayload): Promise<IBaseSelectResult> => {
      if (!watchStudentId) return { optionsResp: [], hasMoreResp: false };
      try {
        const resp = await dispatch(
          getCoursesOfStudent({ studentId: watchStudentId, page: payload.page, limit: DEFAULT_LIMIT_PAGE })
        ).unwrap();
        const optionsResp: IBaseSelectOption[] = (resp.data?.responses || []).map((item) => ({
          value: String(item.id),
          label: item.name,
        }));
        return { optionsResp, hasMoreResp: resp.data?.hasMore ?? false };
      } catch {
        return { optionsResp: [], hasMoreResp: false };
      }
    },
    [dispatch, watchStudentId]
  );

  const fetchModuleOptions = useCallback(
    async (payload: IBaseSelectPayload): Promise<IBaseSelectResult> => {
      if (!watchCourseId) return { optionsResp: [], hasMoreResp: false };
      try {
        const resp = await dispatch(
          getModulesOfCourse({
            courseId: watchCourseId,
            page: payload.page,
            limit: DEFAULT_LIMIT_PAGE,
            type: [ModuleTypeEnum.FINAL_EXAM, ModuleTypeEnum.MIDTERM_EXAM],
          })
        ).unwrap();
        const optionsResp: IBaseSelectOption[] = (resp.data?.responses || []).map((item: any) => ({
          value: String(item.id),
          label: item.name,
        }));
        return { optionsResp, hasMoreResp: resp.data?.hasMore ?? false };
      } catch {
        return { optionsResp: [], hasMoreResp: false };
      }
    },
    [dispatch, watchCourseId]
  );

  const handleBlurPointInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    if (!value) {
      setValue('pointValue', '');
      return;
    }

    const numberValue = parseFloat(value);
    setValue('pointValue', isFinite(numberValue) ? numberValue.toString() : '');
  };

  const handleBlurCreditInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    if (!value) {
      setValue('credits', '');
      return;
    }

    const numberValue = parseFloat(value);
    setValue('credits', isFinite(numberValue) ? numberValue.toString() : '');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCreateExam = async (payload: IBodyCreateExam) => {
    await dispatch(createExam(payload))
      .unwrap()
      .then((res) => {
        reset();
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleSubmitForm = (data: IExamForm) => {
    if (!data) return;

    if (editingExamId) {
      // Update mode
      const payload: IBodyUpdateExam = {
        studentId: data.studentId,
        studentName: data.studentName,
        courseId: data.courseId,
        courseName: data.courseName,
        moduleId: data.moduleId,
        moduleName: data.moduleName,
        note: data.note,
        status: data.status,
        credits: +data.credits,
        pointValue: +data.pointValue,
        value: data.value,
      };
      handleUpdateExam(editingExamId, payload);
    } else {
      // Create mode
      const payload: IBodyCreateExam = {
        studentId: data.studentId,
        studentName: data.studentName,
        courseId: data.courseId,
        courseName: data.courseName,
        moduleId: data.moduleId,
        moduleName: data.moduleName,
        note: data.note,
        status: data.status,
        credits: +data.credits,
        pointValue: +data.pointValue,
        value: data.value,
      };
      handleCreateExam(payload);
    }
  };

  const handleSetTotalExam = () => {
    navigate(`${adminRoute.base}${adminRoute.courses}`);
  };

  const handleDownloadCourseReportPDF = async (reportType: IReportTypeEnum) => {
    if (isExportingReport) return;

    const selectedStudentId = control._formValues?.studentId;
    if (!selectedStudentId) {
      const messageId = generateGuid();
      const toast: IToast = {
        id: messageId,
        message: t('admin_please_select_student', { ns: 'common/base' }),
        type: 'error',
      };
      dispatch(toastActions.addToast(toast));
      setTimeout(() => {
        dispatch(toastActions.removeToast(messageId));
      }, TIMEOUT_ALERT_MILLISECOND);
      return;
    }

    setIsExportingReport(true);
    try {
      const res = await dispatch(
        getCoursesDetailOfStudent({
          studentId: selectedStudentId,
          reportType: reportType, // or 'summary' based on your needs
        })
      ).unwrap();
      const data = res.data;

      const pdfData = {
        student: {
          id: data.student.id,
          identificationNumber: data.student.identificationNumber,
          name: data.student.name,
          lastLogin: data.student.lastLogin,
          loginAt: data.student.loginAt,
        },
        courses: data.courses ?? [],
      };

      if (!pdfData.courses.length) {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_error_message', { ns: 'common/base' }),
          type: 'error',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);
        return;
      }

      await i18n.loadNamespaces(['common/course-details-report']);
      const blob =
        reportType === IReportTypeEnum.NORMAL
          ? await pdf(<DocumentCourseReportPDF data={pdfData} />).toBlob()
          : await pdf(<DocumentCourseActivityReportPDF data={pdfData} />).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        reportType === IReportTypeEnum.NORMAL
          ? `course-report-${pdfData.student.name || 'student'}.pdf`
          : `course-activity-report-${pdfData.student.name || 'student'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      const messageId = generateGuid();
      const toast: IToast = {
        id: messageId,
        message: t('common_error_message', { ns: 'common/base' }),
        type: 'error',
      };
      dispatch(toastActions.addToast(toast));
      setTimeout(() => {
        dispatch(toastActions.removeToast(messageId));
      }, TIMEOUT_ALERT_MILLISECOND);
    } finally {
      setIsExportingReport(false);
    }
  };

  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('administrativeHeader')} width={'100%'}>
          <View as='div' className={cx('administrativeTitle')}>
            <Heading level='h3'>{t('administrative_area_title', { ns: 'admin/administrativeArea' })}</Heading>
            <img
              className={cx('iconArea')}
              src={icons.commonIconExamArea}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </View>

          <Flex gap='small' alignItems='center' wrap='wrap' justifyItems='end'>
            <Button
              color='primary'
              type='button'
              disabled={isExportingReport}
              onClick={() => handleDownloadCourseReportPDF(IReportTypeEnum.NORMAL)}
            >
              <Flex justifyItems='center' alignItems='center' gap='small'>
                <span>{t('administrative_area_download_exam_report_pdf', { ns: 'admin/administrativeArea' })}</span>
                {isExportingReport && <Spinner size='x-small' />}
              </Flex>
            </Button>
            <Button
              color='primary'
              type='button'
              disabled={isExportingReport}
              onClick={() => handleDownloadCourseReportPDF(IReportTypeEnum.ACTIVITY)}
            >
              <Flex justifyItems='center' alignItems='center' gap='small'>
                <span>
                  {t('administrative_area_download_exam_activity_report_pdf', { ns: 'admin/administrativeArea' })}
                </span>
                {isExportingReport && <Spinner size='x-small' />}
              </Flex>
            </Button>
            <Button
              color='primary'
              type='button'
              disabled={quizAnswersExport.isStarting || quizAnswersExport.isRunning}
              onClick={quizAnswersExport.startExport}
            >
              <Flex justifyItems='center' alignItems='center' gap='small'>
                <span>{t('quiz_answers_export_button', { ns: QUIZ_ANSWERS_EXPORT_NAMESPACE })}</span>
                {(quizAnswersExport.isStarting || quizAnswersExport.isRunning) && <Spinner size='x-small' />}
              </Flex>
            </Button>
            <Button color='primary' type='button' onClick={handleSetTotalExam}>
              {t('administrative_area_set_total_exams_button_label', { ns: 'admin/administrativeArea' })}
            </Button>
          </Flex>
        </View>

        <QuizAnswersExportStatus exportState={quizAnswersExport} />

        <View as='div' className={cx('administrativeForm')} margin='medium 0' width={'100%'}>
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <Flex direction={'column'} gap='medium' width={'100%'}>
              <View as='div' className={cx('administrativeFirstContainer')}>
                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='studentId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_select_student_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        {isLoadingEditExam ? (
                          <Flex alignItems='center' gap='small'>
                            <Spinner size='x-small' />
                          </Flex>
                        ) : (
                          <BaseDynamicSelect
                            value={value}
                            selectedLabel={watch('studentName')}
                            callApiFn={fetchStudentOptions}
                            onChange={(option) => {
                              if (!option) return;
                              onChange(option.value);
                              setValue('studentName', option.label);
                              setValue('courseId', '');
                              setValue('courseName', '');
                              setValue('moduleId', '');
                              setValue('moduleName', '');
                            }}
                          />
                        )}
                        {errors?.studentId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.studentId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='courseId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_select_course_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        {isLoadingEditExam ? (
                          <Flex alignItems='center' gap='small'>
                            <Spinner size='x-small' />
                          </Flex>
                        ) : (
                          <BaseDynamicSelect
                            key={watchStudentId}
                            value={value}
                            selectedLabel={watch('courseName')}
                            callApiFn={fetchCourseOptions}
                            disabled={!watchStudentId}
                            onChange={(option) => {
                              if (!option) return;
                              onChange(option.value);
                              setValue('courseName', option.label);
                              setValue('moduleId', '');
                              setValue('moduleName', '');
                            }}
                          />
                        )}
                        {errors?.courseId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.courseId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='moduleId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_select_module_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        {isLoadingEditExam ? (
                          <Flex alignItems='center' gap='small'>
                            <Spinner size='x-small' />
                          </Flex>
                        ) : (
                          <BaseDynamicSelect
                            key={watchCourseId}
                            value={value}
                            selectedLabel={watch('moduleName')}
                            callApiFn={fetchModuleOptions}
                            disabled={!watchCourseId}
                            onChange={(option) => {
                              if (!option) return;
                              onChange(option.value);
                              setValue('moduleName', option.label);
                            }}
                          />
                        )}
                        {errors?.moduleId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.moduleId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='status'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_exam_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <BaseSelect
                          value={value}
                          options={statusOptions}
                          onChange={(option) => {
                            if (!option) return;
                            onChange(option.value);
                          }}
                        />
                        {errors?.status?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.status?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>
              </View>

              <View as='div' className={cx('administrativeSecondContainer')}>
                <View as='div' className={cx('administrativeInput')}>
                  <Controller
                    name='pointValue'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_point_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <TextInput
                          value={value}
                          onBlur={handleBlurPointInput}
                          onChange={(e) => onChange(e.target.value)}
                        />
                        {errors?.pointValue?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.pointValue?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeInput')}>
                  <Controller
                    name='value'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_value_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <TextInput value={value} onChange={(e) => onChange(e.target.value)} />
                        {errors?.value?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.value?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeInput')}>
                  <Controller
                    name='credits'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_credit_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <TextInput
                          value={value}
                          onBlur={handleBlurCreditInput}
                          onChange={(e) => onChange(e.target.value)}
                        />
                        {errors?.credits?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.credits?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeInput')}>
                  <Controller
                    name='note'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_note_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <TextInput value={value} onChange={(e) => onChange(e.target.value)} />
                      </>
                    )}
                  />
                </View>
              </View>
            </Flex>

            <Flex justifyItems='center' margin='medium 0 0 0'>
              <View as='div' className={cx('administrativeButton')}>
                {editingExamId ? (
                  <Flex gap='small'>
                    <Button
                      color='secondary'
                      type='button'
                      onClick={handleCancelEdit}
                      disabled={isLoadingUpdateExam || isLoadingCreateExam || isLoadingEditExam}
                    >
                      {t('cancel_button_label', { ns: 'common/base' })}
                    </Button>
                    <Button
                      color='primary'
                      type='submit'
                      disabled={isLoadingUpdateExam || isLoadingCreateExam || isLoadingEditExam}
                      renderIcon={
                        (isLoadingUpdateExam || isLoadingCreateExam || isLoadingEditExam) && <Spinner size='x-small' />
                      }
                    >
                      {t('save_button_label', { ns: 'common/base' })}
                    </Button>
                  </Flex>
                ) : (
                  <Button
                    color='primary'
                    type='submit'
                    disabled={isLoadingCreateExam}
                    renderIcon={isLoadingCreateExam && <Spinner size='x-small' />}
                  >
                    {t('administrative_area_add_button_label', { ns: 'admin/administrativeArea' })}
                  </Button>
                )}
              </View>
            </Flex>

            <Flex justifyItems='end' margin='medium 0 0 0'>
              <View as='div' className={cx('searchContainer')}>
                <TextInput
                  value={searchText}
                  placeholder={t('administrative_area_search_placeholder', { ns: 'admin/administrativeArea' })}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </View>
            </Flex>
          </form>
        </View>

        <View className={cx('examTableDesktop')}>
          <Table layout='auto'>
            <Table.Head></Table.Head>
            <Table.Body>
              {exams.map((data, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.studentId}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.studentName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.courseName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.moduleName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.pointValue}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.value}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeStatus')}>
                      <img
                        src={
                          data.status === ExamStatusEnum.FAILED
                            ? icons.commonIconStatusFailed
                            : icons.commonIconChangeStatusActive
                        }
                        alt={t('common_alt_img', { ns: 'common/base' })}
                        className={cx('iconStatus')}
                      />
                      <span>
                        {data.status === ExamStatusEnum.FAILED
                          ? t('administrative_area_failed_status', { ns: 'admin/administrativeArea' })
                          : t('administrative_area_passed_status', { ns: 'admin/administrativeArea' })}
                      </span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>
                        {t('administrative_area_credit_value', {
                          ns: 'admin/administrativeArea',
                          quantity: data.credits,
                        })}
                      </span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.note}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <Flex gap='small' alignItems='center'>
                      <IconButton
                        withBorder={false}
                        withBackground={false}
                        color='primary'
                        onClick={() => handleEditExam(data.id)}
                        screenReaderLabel={t('common_edit_label', { ns: 'common/base' })}
                      >
                        <IconEditLine />
                      </IconButton>
                      <IconButton
                        withBorder={false}
                        withBackground={false}
                        color='danger'
                        onClick={() => handleDeleteExam(data.id)}
                        screenReaderLabel={t('common_delete_label', { ns: 'common/base' })}
                      >
                        <IconTrashLine />
                      </IconButton>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>

        <View className={cx('examTableMobile')}>
          {exams.map((data, index) => (
            <View key={index} as='div' className={cx('card')}>
              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_student_id_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.studentId}</span>
              </View>
              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_student_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.studentName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_course_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.courseName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_module_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.moduleName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_point_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.pointValue}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_value_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.value}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_credit_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>
                  {t('administrative_area_credit_value', {
                    ns: 'admin/administrativeArea',
                    quantity: data.credits,
                  })}
                </span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_status_label', { ns: 'admin/administrativeArea' })}
                </span>

                <View as='div' className={cx('cardItem', 'status')}>
                  <img
                    src={
                      data.status === ExamStatusEnum.FAILED
                        ? icons.commonIconStatusFailed
                        : icons.commonIconChangeStatusActive
                    }
                    alt='Status Icon'
                    className={cx('iconStatus')}
                  />
                  <span>
                    {data.status === ExamStatusEnum.FAILED
                      ? t('administrative_area_failed_status', { ns: 'admin/administrativeArea' })
                      : t('administrative_area_passed_status', { ns: 'admin/administrativeArea' })}
                  </span>
                </View>
              </View>

              {data.note && (
                <View as='div' className={cx('cardItem')}>
                  <span className={cx('cardLabel')}>
                    {t('administrative_area_table_note_label', { ns: 'admin/administrativeArea' })}
                  </span>
                  <span>{data.note}</span>
                </View>
              )}

              <Flex gap='small' alignItems='center' margin='small 0'>
                <IconButton
                  withBorder={false}
                  withBackground={false}
                  color='primary'
                  onClick={() => handleEditExam(data.id)}
                  screenReaderLabel={t('common_edit_label', { ns: 'common/base' })}
                >
                  <IconEditLine />
                </IconButton>
                <IconButton
                  withBorder={false}
                  withBackground={false}
                  color='danger'
                  onClick={() => handleDeleteExam(data.id)}
                  screenReaderLabel={t('common_delete_label', { ns: 'common/base' })}
                >
                  <IconTrashLine />
                </IconButton>
              </Flex>
            </View>
          ))}

          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>
      </div>

      <ModalDeleteExam
        isModalOpen={isDeleteModalOpen}
        examId={examIdToDelete}
        onClose={handleCloseDeleteModal}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default AdministrativeArea;
